import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Shield, Printer, Check, X, RefreshCw, Download } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

export default function InsurancePortal() {
  const { branch } = useAuth();
  const [claims, setClaims] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [printFor, setPrintFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/insurance/claims", {
        params: { branch_code: branch.code, status_filter: filter },
      });
      setClaims(data);
    } catch (err) { toast.error(formatApiError(err, "Load failed")); }
    finally { setLoading(false); }
  }, [branch?.code, filter]);
  useEffect(() => { load(); }, [load]);

  const decide = async (a, decision) => {
    try {
      await api.patch(`/admissions/${a.id}/validate-insurance?decision=${decision}`);
      toast.success(`Claim ${decision}`);
      load();
    } catch (err) { toast.error(formatApiError(err, "Update failed")); }
  };

  return (
    <div className="max-w-6xl mx-auto animate-fade-up" data-testid="insurance-portal">
      <div className="text-xs uppercase tracking-[0.28em] text-[#5C5652] mb-3 ornament-dot">
        Manager · Insurance validation · {branch.name}
      </div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl text-forest mb-1 flex items-center gap-2">
            <Shield className="h-7 w-7" strokeWidth={1.5} /> Insurance claims
          </h1>
          <p className="text-[#5C5652]">Review flagged admissions and print the full bill pack (room + treatment + pharmacy) in one click.</p>
        </div>
        <div className="flex gap-2">
          <div className="flex bg-[#F9F6F0] border border-sand rounded-lg p-1">
            {["pending", "validated", "rejected", "all"].map((s) => (
              <button key={s} onClick={() => setFilter(s)} data-testid={`ins-filter-${s}`}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider font-bold rounded ${filter === s ? "bg-forest text-white" : "text-[#5C5652]"}`}>
                {s}
              </button>
            ))}
          </div>
          <button onClick={load} data-testid="ins-refresh" className="h-9 px-3 rounded-lg border border-sand bg-white hover:bg-[#F9F6F0] text-sm font-semibold inline-flex items-center gap-2">
            <RefreshCw className="h-4 w-4" strokeWidth={1.75} /> Refresh
          </button>
        </div>
      </div>

      <div className="bg-white border border-sand rounded-lg overflow-hidden">
        {loading ? <div className="p-8 text-sm text-[#5C5652]">Loading…</div>
          : claims.length === 0 ? <div className="p-10 text-center text-sm text-[#5C5652]">No {filter} claims.</div>
          : <ul className="divide-y divide-[#E8E3D9]" data-testid="ins-claims-list">
              {claims.map((c) => (
                <li key={c.id} className="p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`ins-claim-${c.admission_no}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[#2A2624]">{c.patient_name} <span className="text-xs text-[#5C5652] ml-2">{c.admission_no} · {c.mrn}</span></div>
                    <div className="text-xs text-[#5C5652]">
                      {c.insurance_provider || "—"} · Policy {c.insurance_policy_no || "—"} · Room {c.room_number} · ₹ {Number(c.grand_total).toFixed(2)}
                    </div>
                    <div className="text-[10px] mt-0.5">
                      <StatusBadge s={c.insurance_status} />
                      {c.insurance_validated_by && <span className="ml-2 text-[#5C5652]">by {c.insurance_validated_by}</span>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setPrintFor(c)} data-testid={`ins-print-${c.admission_no}`}
                      className="h-9 px-3 rounded-lg border border-sand hover:bg-[#F9F6F0] text-sm font-semibold inline-flex items-center gap-1">
                      <Printer className="h-4 w-4" strokeWidth={1.75} /> Print pack
                    </button>
                    {c.insurance_status === "pending" && (<>
                      <button onClick={() => decide(c, "validated")} data-testid={`ins-approve-${c.admission_no}`}
                        className="h-9 px-3 rounded-lg bg-forest text-white text-sm font-semibold hover:bg-forest-hover inline-flex items-center gap-1">
                        <Check className="h-4 w-4" strokeWidth={2} /> Approve
                      </button>
                      <button onClick={() => decide(c, "rejected")} data-testid={`ins-reject-${c.admission_no}`}
                        className="h-9 px-3 rounded-lg border border-[#C25946] text-[#C25946] text-sm font-semibold hover:bg-[#C25946]/10 inline-flex items-center gap-1">
                        <X className="h-4 w-4" strokeWidth={2} /> Reject
                      </button>
                    </>)}
                  </div>
                </li>
              ))}
            </ul>}
      </div>

      {printFor && <BillPack claim={printFor} branch={branch} onClose={() => setPrintFor(null)} />}
    </div>
  );
}

function StatusBadge({ s }) {
  const map = {
    pending: { c: "bg-[#E5A93D]/15 text-[#8A5F1B]", l: "Pending" },
    validated: { c: "bg-[#1A4331]/10 text-forest", l: "Validated" },
    rejected: { c: "bg-[#C25946]/10 text-[#C25946]", l: "Rejected" },
    none: { c: "bg-[#F0EBDE] text-[#5C5652]", l: "—" },
  };
  const v = map[s] || map.none;
  return <span className={`inline-block px-2 py-0.5 rounded font-bold tracking-wider text-[10px] ${v.c}`}>{v.l}</span>;
}

function BillPack({ claim, branch, onClose }) {
  const bill = claim.pharmacy_bill;
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const downloadPDF = async () => {
    if (!ref.current) return;
    setBusy(true);
    try {
      const canvas = await html2canvas(ref.current, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const w = pdf.internal.pageSize.getWidth() - 20;
      const h = (canvas.height * w) / canvas.width;
      let position = 10;
      let heightLeft = h;
      pdf.addImage(img, "PNG", 10, position, w, h);
      heightLeft -= (pdf.internal.pageSize.getHeight() - 20);
      while (heightLeft > 0) {
        position = heightLeft - h + 10;
        pdf.addPage();
        pdf.addImage(img, "PNG", 10, position, w, h);
        heightLeft -= (pdf.internal.pageSize.getHeight() - 20);
      }
      pdf.save(`insurance-${claim.admission_no}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("PDF generation failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-[#2A2624]/60 z-50 flex items-start sm:items-center justify-center p-4 print:static print:bg-white print:p-0" onClick={onClose} data-testid="ins-bill-pack">
      <div className="bg-white w-full max-w-3xl max-h-[92vh] flex flex-col rounded-lg border border-sand print:max-h-none print:rounded-none print:border-none print:block" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-3 border-b border-sand flex items-center justify-between print:hidden bg-white shrink-0">
          <div className="text-xs font-bold uppercase tracking-wider text-[#5C5652]">Bill pack · {claim.admission_no}</div>
          <div className="flex gap-2">
            <button onClick={() => window.print()} data-testid="ins-pack-print" className="h-9 px-3 rounded-lg border border-sand hover:bg-[#F9F6F0] text-sm font-semibold inline-flex items-center gap-2">
              <Printer className="h-4 w-4" strokeWidth={1.75} /> Print
            </button>
            <button onClick={downloadPDF} disabled={busy} data-testid="ins-pack-pdf" className="h-9 px-3 rounded-lg bg-forest text-white text-sm font-semibold hover:bg-forest-hover inline-flex items-center gap-2 disabled:opacity-60">
              <Download className="h-4 w-4" strokeWidth={1.75} /> {busy ? "Building…" : "Download PDF"}
            </button>
            <button onClick={onClose} className="h-9 w-9 rounded-lg border border-sand hover:bg-[#F9F6F0] flex items-center justify-center"><X className="h-4 w-4" /></button>
          </div>
        </div>
        <div ref={ref} className="p-8 print:p-6 text-sm overflow-y-auto print:overflow-visible">
          <div className="text-center border-b-2 border-forest pb-4 mb-4">
            <img src="https://customer-assets-cm19k8pv.emergentagent.net/job_ayurveda-care-27/artifacts/hi17r931_image-removebg-preview.png" alt="Badarinath" className="h-12 mx-auto object-contain" crossOrigin="anonymous" />
            <div className="mt-2 text-xs uppercase tracking-wider text-[#5C5652]">
              Insurance claim · {branch?.name} · {claim.insurance_provider || "—"}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
            <div><div className="text-[10px] uppercase text-[#5C5652]">Patient</div><div className="font-semibold">{claim.patient_name}</div></div>
            <div><div className="text-[10px] uppercase text-[#5C5652]">Policy</div><div className="font-mono">{claim.insurance_policy_no || "—"}</div></div>
            <div><div className="text-[10px] uppercase text-[#5C5652]">MRN</div><div className="font-mono">{claim.mrn}</div></div>
            <div><div className="text-[10px] uppercase text-[#5C5652]">Admission</div><div className="font-mono">{claim.admission_no}</div></div>
          </div>

          <Section title="1 · Room rent">
            <table className="w-full text-sm border border-sand rounded overflow-hidden">
              <tbody className="divide-y divide-[#E8E3D9]">
                <tr><td className="px-3 py-2">Room {claim.room_number} — {claim.days} day(s) × ₹ {Number(claim.tariff_per_day).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right font-mono">{Number(claim.room_charges_total).toFixed(2)}</td></tr>
              </tbody>
            </table>
          </Section>

          <Section title="2 · Treatment & other charges">
            {(claim.charges || []).length === 0 ? <div className="text-[#5C5652] italic text-xs">No additional charges recorded.</div>
              : <table className="w-full text-sm border border-sand rounded overflow-hidden">
                  <tbody className="divide-y divide-[#E8E3D9]">
                    {claim.charges.map((c, i) => (<tr key={i}>
                      <td className="px-3 py-2">{c.label}</td>
                      <td className="px-3 py-2 text-right font-mono">{Number(c.amount).toFixed(2)}</td>
                    </tr>))}
                  </tbody>
                </table>}
          </Section>

          <Section title="3 · Pharmacy bill">
            {!bill ? <div className="text-[#5C5652] italic text-xs">No pharmacy bill on file.</div>
              : <table className="w-full text-sm border border-sand rounded overflow-hidden">
                  <thead className="bg-[#F9F6F0] text-[10px] uppercase tracking-wider text-[#5C5652]">
                    <tr><th className="text-left px-3 py-2">Item</th><th className="text-right px-3 py-2">Qty</th><th className="text-right px-3 py-2">₹</th></tr>
                  </thead>
                  <tbody className="divide-y divide-[#E8E3D9]">
                    {(bill.items || []).map((it, i) => (<tr key={i}>
                      <td className="px-3 py-2">{it.name}</td>
                      <td className="px-3 py-2 text-right font-mono">{it.qty}</td>
                      <td className="px-3 py-2 text-right font-mono">{(Number(it.qty) * Number(it.price)).toFixed(2)}</td>
                    </tr>))}
                    <tr className="bg-[#F9F6F0]"><td colSpan={2} className="px-3 py-2 font-semibold">Pharmacy total</td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-forest">{Number(bill.total).toFixed(2)}</td></tr>
                  </tbody>
                </table>}
          </Section>

          <div className="mt-6 p-4 rounded bg-[#F9F6F0] border border-sand flex items-center justify-between">
            <div className="font-display text-xl text-forest">Total claim</div>
            <div className="font-mono text-2xl font-bold text-forest">
              ₹ {(Number(claim.grand_total) + Number(bill?.total || 0)).toFixed(2)}
            </div>
          </div>
        </div>
        <style>{`@media print { @page { size: A4; margin: 12mm; } body { background: white !important; } .print\\:hidden { display: none !important; } }`}</style>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (<div className="mb-4">
    <div className="text-xs font-bold uppercase tracking-wider text-[#5C5652] mb-2">{title}</div>
    {children}
  </div>);
}
